/**
 * KERN Feedback Widget
 *
 * Ein einbettbares Feedback-Tool für KERN-basierte Webanwendungen.
 *
 * @example Slot-/Mount-Einbindung (wie beim KERN Chatbot):
 * ```html
 * <div id="kern-feedback"></div>
 * <script src="https://feedback.kern-ux.de/widget/feedback-widget.js"></script>
 * <script>
 *   KernFeedback.mountFeedbackWidget('#kern-feedback', {
 *     apiKey: 'YOUR_API_KEY',
 *     apiUrl: 'https://feedback.kern-ux.de/api/v1',
 *     lightDarkMode: 'auto', // "auto" | "light" | "dark"
 *     primaryColor: '#004B76',
 *     buttonText: 'KERN Feedback',
 *   });
 * </script>
 * ```
 *
 * @example Script-Tag Einbindung (Auto-Init über data-Attribute):
 * ```html
 * <div id="kern-feedback"></div>
 * <script
 *   src="https://feedback.kern-ux.de/widget/feedback-widget.js"
 *   data-api-key="YOUR_API_KEY"
 *   data-selector="#kern-feedback"
 *   data-position="bottom-right"
 *   async
 * ></script>
 * ```
 *
 * @example Programmatische Einbindung:
 * ```js
 * import { mountFeedbackWidget } from '@kern/feedback-widget';
 *
 * const mount = mountFeedbackWidget('#kern-feedback', {
 *   apiKey: 'YOUR_API_KEY',
 *   onSubmit: (result) => console.log('Feedback gesendet:', result),
 * });
 *
 * // später, z. B. beim Unmount einer SPA-Komponente:
 * mount.unmount();
 * ```
 */

export { FeedbackWidget } from './FeedbackWidget.js';
export type { FeedbackWidgetConfig, FeedbackData, FeedbackSubmitResult } from './types.js';
export { mountFeedbackWidget, getMountedFeedbackWidget, MOUNT_MARKER_ATTRIBUTE } from './mount.js';
export { findFeedbackWidgetInDocument } from './mount.js';
export type { FeedbackWidgetMount, MountFeedbackWidgetOptions } from './mount.js';
export {
  ScreenshotManager,
  ScrollLock,
  captureScreenshot,
  captureNativeScreenshot,
  isNativeScreenCaptureSupported,
  isMobileDevice,
  isIOSDevice,
  isAndroidDevice,
} from './screenshot.js';
export type {
  ScreenshotResult,
  ScreenshotCaptureState,
  ScreenshotCaptureMethod,
  ScreenshotStateCallback,
} from './screenshot.js';
export {
  validateImageFile,
  getAllowedMimeTypes,
  getFileInputAcceptAttribute,
  getAllowedFormatsText,
  readFileAsArrayBuffer,
} from './imageValidation.js';
export type { ImageValidationResult, AllowedImageFormat } from './imageValidation.js';
/**
 * Version of this widget build, reachable as `KernFeedback.VERSION`.
 *
 * Widget assets have stable filenames (embedding sites hardcode
 * `/widget/feedback-widget.js`), so they are served with `Cache-Control:
 * no-cache` and revalidated on every load rather than cache-busted via the
 * filename. This constant makes the build that a client actually loaded
 * identifiable — from the browser console on any embedding site and from the
 * deployment postflight check.
 */
export { VERSION, VERSION_INFO } from './version.js';

import type { FeedbackWidgetConfig } from './types.js';

/**
 * The `<script>` element that loaded this bundle, captured while the module
 * body is still executing — `document.currentScript` is only meaningful at
 * that moment and is `null` inside the deferred DOMContentLoaded callback.
 */
const LOADER_SCRIPT: HTMLScriptElement | null =
  typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

// Auto-Init bei Script-Tag Einbindung
(function autoInit() {
  // Nur im Browser ausführen
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  /**
   * Locate the configuring `<script>` tag. Preference order:
   *   1. The script that loaded this bundle, if it carries `data-api-key`.
   *   2. A `data-api-key` script whose `src` points at this widget bundle.
   *   3. The last `data-api-key` script (legacy behaviour).
   */
  const findConfigScript = (): Element | null => {
    if (LOADER_SCRIPT?.hasAttribute('data-api-key')) {
      return LOADER_SCRIPT;
    }
    const scripts = Array.from(document.querySelectorAll('script[data-api-key]'));
    return (
      scripts.filter((s) => (s.getAttribute('src') ?? '').includes('feedback-widget')).pop() ??
      scripts.pop() ??
      null
    );
  };

  // Auf DOMContentLoaded warten
  const init = () => {
    const currentScript = findConfigScript();

    if (!currentScript) {
      return;
    }

    const apiKey = currentScript.getAttribute('data-api-key');
    if (!apiKey) {
      console.warn('KERN Feedback: data-api-key Attribut fehlt');
      return;
    }

    // Konfiguration aus data-Attributen lesen
    const config: Record<string, string | number | boolean> = { apiKey };

    const position = currentScript.getAttribute('data-position');
    if (position) config.position = position;

    // NEW slot/mount integration (chatbot style): `data-selector` names a
    // placeholder element and routes through `mountFeedbackWidget()`.
    const mountSelector = currentScript.getAttribute('data-selector');

    // LEGACY integration: `data-trigger-container` keeps the original code
    // path (plain `new FeedbackWidget({ triggerContainer })` + `init()`),
    // unchanged, so existing embeds behave exactly as before.
    const legacyTriggerContainer = currentScript.getAttribute('data-trigger-container');

    if (mountSelector && legacyTriggerContainer) {
      console.warn(
        'KERN Feedback: data-selector and data-trigger-container are both set — data-selector (slot mount) wins.'
      );
    }

    const primaryColor = currentScript.getAttribute('data-primary-color');
    if (primaryColor) config.primaryColor = primaryColor;

    const buttonText = currentScript.getAttribute('data-button-text');
    if (buttonText) config.buttonText = buttonText;

    const dialogTitle = currentScript.getAttribute('data-dialog-title');
    if (dialogTitle) config.dialogTitle = dialogTitle;

    const lightDarkMode = currentScript.getAttribute('data-light-dark-mode');
    if (lightDarkMode === 'auto' || lightDarkMode === 'light' || lightDarkMode === 'dark') {
      config.lightDarkMode = lightDarkMode;
    }

    const apiUrl = currentScript.getAttribute('data-api-url');
    if (apiUrl) config.apiUrl = apiUrl;

    const showEmail = currentScript.getAttribute('data-show-email');
    if (showEmail === 'false') config.showEmail = false;

    const showRating = currentScript.getAttribute('data-show-rating');
    if (showRating === 'false') config.showRating = false;

    const enableScreenshot = currentScript.getAttribute('data-enable-screenshot');
    if (enableScreenshot === 'false') config.enableScreenshot = false;

    const screenshotMethod = currentScript.getAttribute('data-screenshot-method');
    if (
      screenshotMethod === 'dom' ||
      screenshotMethod === 'native' ||
      screenshotMethod === 'auto'
    ) {
      config.screenshotMethod = screenshotMethod;
    }

    const debugScreenshot = currentScript.getAttribute('data-debug-screenshot');
    if (debugScreenshot === 'true') config.debugScreenshot = true;

    const zIndex = currentScript.getAttribute('data-z-index');
    if (zIndex && Number.isFinite(Number(zIndex))) config.zIndex = Number(zIndex);

    // `data-auto-init="false"` lädt und konfiguriert das Widget, hängt es aber
    // noch nicht ein — die Host-Seite ruft später `KernFeedback.init()` auf.
    const deferInit = currentScript.getAttribute('data-auto-init') === 'false';

    // Widget initialisieren
    Promise.all([import('./FeedbackWidget.js'), import('./mount.js')]).then(
      ([
        { FeedbackWidget },
        { mountFeedbackWidget, exposeGlobalFeedbackApi, findFeedbackWidgetInDocument },
      ]) => {
        // Guard against a second execution of this bundle on the same page —
        // e.g. a site that injects the loader globally (Docusaurus `headTags`)
        // AND repeats the <script> in a single page's content. Without this,
        // each execution would build its own widget, yielding duplicate
        // triggers, duplicate element IDs and a broken page-scroll lock.
        //
        // The re-executed IIFE also creates a FRESH `window.KernFeedback`
        // namespace, so the existing instance has to be re-published onto it,
        // otherwise the documented `window.KernFeedback.open()` would be gone.
        const existing = findFeedbackWidgetInDocument();
        if (existing) {
          console.warn(
            'KERN Feedback: a widget is already present on this page — skipping auto-initialisation.'
          );
          exposeGlobalFeedbackApi(existing);
          return;
        }

        // Never let the constructor self-initialise: init() is driven from
        // here so it happens exactly once (and not at all when deferred).
        const widgetConfig = { ...config, autoInit: false } as unknown as FeedbackWidgetConfig;

        // --- New slot/mount path -------------------------------------------
        if (mountSelector && !deferInit) {
          try {
            mountFeedbackWidget(mountSelector, widgetConfig);
            return;
          } catch (error) {
            console.warn(
              `KERN Feedback: ${(error as Error).message} — es wird auf den floatenden Trigger zurückgefallen.`
            );
          }
        }

        // --- Legacy path (unchanged behaviour) -----------------------------
        const legacyContainer = mountSelector ?? legacyTriggerContainer;
        if (legacyContainer) {
          widgetConfig.triggerContainer = legacyContainer;
        }

        const widget = new FeedbackWidget(widgetConfig);
        if (!deferInit) {
          widget.init();
        }

        // Global verfügbar machen, ohne den Export-Namespace zu überschreiben.
        exposeGlobalFeedbackApi(widget);
      }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
